import { useAuthActions } from "@convex-dev/auth/react";
import { useQuery } from "convex/react";
import { useState } from "react";
import { Building2, ClipboardList, Coins, LayoutList, Settings } from "lucide-react";
import { Link, useLocation } from "react-router";
import {
  Sidebar,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  type SidebarItem,
} from "@repo/ui";
import { motPluriel } from "@repo/utils";
import { api } from "@convex/_generated/api";
import {
  aEntretienStructure,
  estGestionnaireClient,
  useCurrentUser,
} from "../lib/useCurrentUser";

/** Racine des écrans campagnes — `/` et `/campagnes` mènent au même écran. */
const CHEMINS_CAMPAGNES = ["/", "/campagnes", "/p"];

function estActif(pathname: string, cible: string): boolean {
  if (cible === "/campagnes") {
    return CHEMINS_CAMPAGNES.some(
      (c) => pathname === c || (c !== "/" && pathname.startsWith(`${c}/`)),
    );
  }
  return pathname === cible || pathname.startsWith(`${cible}/`);
}

export function ClientSidebar() {
  const { signOut } = useAuthActions();
  const user = useQuery(api.auth.currentUser);
  const currentUser = useCurrentUser();
  const org = useQuery(api.organisation.monOrganisation);
  const { pathname } = useLocation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const displayName = user?.name ?? user?.email ?? "Administrateur";
  const initials = displayName
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  // Le nom de l'organisation est désormais porté par la bande dédiée en tête de
  // sidebar : le bloc utilisateur situe la PERSONNE (son groupe, sinon son
  // email) plutôt que de répéter le locataire deux fois dans la même colonne.
  const sousTitreUtilisateur =
    currentUser?.groupeNom ??
    (user?.email && user.email !== displayName ? user.email : "Mon compte");

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const items: SidebarItem[] = [
    {
      to: "/campagnes",
      label: "Campagnes",
      icon: <LayoutList className="h-4 w-4" />,
      actif: estActif(pathname, "/campagnes"),
    },
    // Scorecards : gestionnaires seulement, les seuls à pouvoir les modifier,
    // et seulement si l'organisation a l'entretien structuré.
    ...(estGestionnaireClient(currentUser) && aEntretienStructure(currentUser)
      ? [
          {
            to: "/scorecards",
            label: "Scorecards",
            icon: <ClipboardList className="h-4 w-4" />,
            actif: estActif(pathname, "/scorecards"),
          },
        ]
      : []),
    {
      to: "/organisation",
      label: "Mon organisation",
      icon: <Building2 className="h-4 w-4" />,
      actif: estActif(pathname, "/organisation"),
    },
    {
      to: "/parametres",
      label: "Paramètres",
      icon: <Settings className="h-4 w-4" />,
      actif: estActif(pathname, "/parametres"),
    },
  ];

  const credits = org?.credits;
  // Solde négatif : consommation au-delà de ce qui a été acheté. Signalé par la
  // couleur d'accent, pas en rouge — rien n'est bloqué, ce n'est pas une erreur.
  const couleurCredits =
    credits !== undefined && credits < 0
      ? "text-charte-accent"
      : "text-charte-soft";

  /**
   * Solde toujours sous les yeux, cliquable vers Paramètres où il est détaillé.
   * Replié, seul le nombre subsiste : « 12 crédits » ne tient pas sur 4 rem, et
   * le tooltip porte le libellé complet.
   */
  const encartCredits = ({ replie }: { replie: boolean }) => {
    const libelle =
      credits === undefined
        ? "Crédits"
        : `${credits} ${motPluriel(credits, "crédit")}`;

    const contenu = (
      <Link
        to="/parametres"
        aria-label={`${libelle} — voir les paramètres`}
        className={`flex rounded-surface transition-colors hover:bg-charte-hover ${
          // Replié, l'icône et le nombre s'empilent : côte à côte sur 4 rem, le
          // nombre était réduit à une taille bien plus petite que l'icône et
          // devenait le détail alors que c'est LUI qu'on vient lire.
          replie
            ? "flex-col items-center gap-0.5 px-1 py-1.5"
            : "items-center gap-2 px-2 py-1.5 text-sm"
        } ${couleurCredits}`}
      >
        <Coins
          className={replie ? "h-3.5 w-3.5 shrink-0" : "h-4 w-4 shrink-0"}
          aria-hidden
        />
        {replie ? (
          <span className="text-sm font-semibold leading-none tabular-nums">
            {credits ?? "—"}
          </span>
        ) : (
          <span className="truncate font-medium tabular-nums">{libelle}</span>
        )}
      </Link>
    );

    return (
      <Tooltip>
        <TooltipTrigger asChild>{contenu}</TooltipTrigger>
        <TooltipContent side="right">
          {credits === undefined
            ? "Crédits de l'organisation"
            : credits < 0
              ? `${libelle} consommés au-delà du solde`
              : `${libelle} disponibles`}
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <Sidebar
      items={items}
      encart={encartCredits}
      clePersistance="portail-client:sidebar-repliee"
      logoUrl={currentUser?.organisationLogoUrl}
      organisationNom={currentUser?.organisationNom}
      renderLink={({ to, className, children, onNavigate, ariaLabel }) => (
        <Link
          key={to}
          to={to}
          className={className}
          onClick={onNavigate}
          aria-label={ariaLabel}
        >
          {children}
        </Link>
      )}
      userInfo={{
        displayName,
        subtitle: sousTitreUtilisateur,
        initials: initials || "AD",
      }}
      onLogout={() => void handleLogout()}
      isLoggingOut={isLoggingOut}
    />
  );
}
